import { create } from "zustand";
import {
  Alternative,
  Item,
  TranscriptResultStream,
} from "@aws-sdk/client-transcribe-streaming";
import { useEditor } from "tiptap/EditorStore";
import { useReplaceList } from "./replace-list-store";
import { toast } from "react-toastify";

type ReplaceItem = {
  to?: string;
  next?: ReplaceList;
};

type ReplaceList = {
  [key: string]: ReplaceItem;
};

type AlternativeWithReplacement = Alternative & { replacedItems?: Item[] };

type CaptionHandlerStore = {
  transcriptionData: AlternativeWithReplacement | null;
  replaceItems: ReplaceList;
  handleTranscriptionEvent: (event: TranscriptResultStream) => void;
  handleTranscriptionData: (
    data: AlternativeWithReplacement,
    isPartial: boolean
  ) => void;
  handleReplace: (items: Item[], iterations?: number) => Item[];
  insertOnNewLine: (
    index: number,
    currentItems: Item[],
    items: Item[]
  ) => number;
  replay: () => void;
  store: () => void;
  clearRecording: () => void;
};

let playing = false;
const store: {
  record: { event: AlternativeWithReplacement; isPartial: boolean }[];
} = {
  record: [],
};

export const useCaptionHandler = create<CaptionHandlerStore>()((set, get) => ({
  transcriptionData: null,
  replaceItems: {
    test: { to: "testord" },
    bäst: { next: { i: { next: { världen: { to: "bäst i sörmland" } } } } },
    eh: { to: "" },
  },
  handleTranscriptionEvent(event) {
    const { handleTranscriptionData } = get();
    if (event?.TranscriptEvent?.Transcript?.Results) {
      for (const result of event?.TranscriptEvent?.Transcript.Results) {
        if (result?.Alternatives && result?.Alternatives[0].Items) {
          if (result.Alternatives.length > 1) {
            console.log(`Got multiple alternatives: ${result.Alternatives}`);
          }
          handleTranscriptionData(result.Alternatives[0], !!result.IsPartial);
          console.log(
            "Transcription",
            event.TranscriptEvent.Transcript.Results[0].Alternatives?.[0],
            event.TranscriptEvent.Transcript.Results[0].IsPartial
          );
        }
      }
    } else {
      console.log("Empty transcription event");
    }
  },
  insertOnNewLine(index, currentItems, items) {
    const prevWord = items[index - 1];
    const currentWord = items[index];
    if (
      prevWord &&
      prevWord.Content === "." &&
      currentWord.StartTime &&
      prevWord.EndTime &&
      currentWord.StartTime - prevWord.EndTime > 2
    ) {
      return 2;
    }
    if (index === 0 && currentItems.length === 0) {
      return 1;
    }
    return 0;
  },
  handleTranscriptionData(data, isPartial) {
    let items = data.Items;
    if (!playing) {
      store.record.push({ event: data, isPartial });
    }
    if (items && items[0].Type !== "punctuation") {
      const { transcriptionData, handleReplace, insertOnNewLine } = get();
      let transcriptionItems =
        transcriptionData?.replacedItems || transcriptionData?.Items || [];
      const replacedItems = handleReplace(items);
      if (replacedItems !== data.Items) {
        data.replacedItems = replacedItems;
        items = replacedItems;
      }
      const { editor } = useEditor.getState();
      for (let i = 0; i < items.length; i++) {
        if (!transcriptionItems[i]) {
          editor.commands.addTranscriptWord(
            items[i],
            insertOnNewLine(i, transcriptionItems, items)
          );
        } else if (transcriptionItems[i].Content !== items[i].Content) {
          editor.commands.updateTranscriptWord(
            transcriptionItems[i].Content!,
            items[i].Content!,
            0,
            i === items.length - 1 && items.length < transcriptionItems.length
          );
        }
      }
      if (isPartial) {
        set({ transcriptionData: data });
      } else {
        set({ transcriptionData: null });
      }
    }
  },
  handleReplace(items: Item[], iterations: number = 0) {
    const { handleReplace } = get();
    const { active } = useReplaceList.getState();
    for (let i = 0; i < items.length; i++) {
      const replaceResults = canReplace(items, i, active);
      if (replaceResults.at >= 0) {
        const newItems = makeReplace(items, replaceResults);
        if (iterations > 20) {
          toast.error(
            "Number or replacement iterations exceeded 20. You probably have a loop in your corrections."
          );
          return newItems;
        }
        return handleReplace(newItems, iterations + 1);
      }
    }
    return items;
  },
  store() {
    if (store.record.length) {
      toast.info("Stored transcription records.");
      localStorage.setItem("storedtranscription", JSON.stringify(store.record));
    } else {
      toast.info("Nothing to store.");
    }
  },
  async replay() {
    playing = true;
    const { handleTranscriptionData } = get();
    const data = localStorage.getItem("storedtranscription");
    const recordedData = JSON.parse(data || "[]");
    for (const part of recordedData) {
      await new Promise((resolve) => setTimeout(resolve, 10));
      handleTranscriptionData(part.event, part.isPartial);
    }
  },
  clearRecording() {
    toast.info("Cleared transcription records.");
    store.record = [];
  },
}));

type ReplaceResults = {
  at: number;
  words: number;
  replacement: string;
};

function canReplace(
  items: Item[],
  index: number,
  replaceList?: ReplaceList,
  count: number = 1
): ReplaceResults {
  if (!replaceList || index >= items.length) {
    return { at: -1, words: 0, replacement: "" };
  }
  const replaceItem = replaceList[items[index].Content!.toLowerCase()];
  if (replaceItem) {
    const chainedReplace = canReplace(
      items,
      index + 1,
      replaceItem.next,
      count + 1
    );
    if (chainedReplace.at >= 0) {
      return { ...chainedReplace, at: index };
    }
    if (replaceItem.to !== undefined) {
      return {
        at: index,
        words: count,
        replacement: replaceItem.to,
      };
    }
  }

  return { at: -1, words: 0, replacement: "" };
}

function makeReplace(items: Item[], replaceResult: ReplaceResults): Item[] {
  if (replaceResult.replacement === "") {
    const wordBefore = items[replaceResult.at - 1];
    const wordAfter = items[replaceResult.at + replaceResult.words];

    if (wordBefore?.Content === ",") {
      replaceResult.at -= 1;
      replaceResult.words += 1;
    }
    if (wordAfter?.Content === ",") {
      replaceResult.words += 1;
    }

    return items
      .slice(0, replaceResult.at)
      .concat(items.slice(replaceResult.at + replaceResult.words));
  }
  const originalWord = items[replaceResult.at];
  const newItems = replaceResult.replacement.split(" ").map((word) => ({
    ...originalWord,
    Content: word,
  }));
  return items
    .slice(0, replaceResult.at)
    .concat(newItems)
    .concat(items.slice(replaceResult.at + replaceResult.words));
}
